import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from 'antd';
import Button from 'components/generalUI/Button';
import priceFormat from 'utils/formats/priceFormat';
import getTicketDisplayName from 'utils/helpers/getTicketDisplayName';
import { TicketSummaryViewModel } from 'views/TicketSummary/ticketSummary.viewModel';
import styles from './PurchaseSummaryCard.module.scss';

type PurchaseSummaryCardProps = Pick<
  TicketSummaryViewModel,
  | 'items'
  | 'grill'
  | 'canopy'
  | 'subTotal'
  | 'discountAmount'
  | 'giftCardAmount'
  | 'total'
  | 'paymentMethods'
  | 'selectedPaymentMethodId'
  | 'onSelectPaymentMethod'
  | 'canPay'
  | 'loading'
  | 'onPay'
>;

const PurchaseSummaryCard: FC<PurchaseSummaryCardProps> = (
  props: PurchaseSummaryCardProps
) => {
  const { t } = useTranslation();
  const {
    items,
    grill,
    canopy,
    subTotal,
    discountAmount,
    giftCardAmount,
    total,
    paymentMethods,
    selectedPaymentMethodId,
    onSelectPaymentMethod,
    canPay,
    loading,
    onPay,
  } = props;

  return (
    <div className={styles.Card}>
      <h2 className={styles.Title}>{t('pages.ticketSummary.title')}</h2>

      <Divider className={styles.Divider} />

      {items.map((item) => (
        <div className={styles.Item} key={item.id}>
          <h3 className={styles.ItemName}>
            {t('pages.ticketSummary.ticketName', {
              name: getTicketDisplayName(item.name),
            })}
          </h3>
          <div className={styles.Row}>
            <div className={styles.RowLabel}>
              <p className={styles.Label}>{t('pages.ticketSummary.tickets')}</p>
              {item.adultTicketCount > 0 && (
                <p className={styles.Breakdown}>
                  {t('pages.ticketSummary.adultTickets', {
                    amount: item.adultTicketCount,
                  })}
                </p>
              )}
              {item.otherTicketCount > 0 && (
                <p className={styles.Breakdown}>
                  {t('pages.ticketSummary.otherTickets', {
                    amount: item.otherTicketCount,
                  })}
                </p>
              )}
            </div>
            <span className={styles.Value}>{priceFormat.format(item.price)}</span>
          </div>
        </div>
      ))}

      {grill && (
        <div className={`${styles.Row} ${styles.GrillRow}`}>
          <p className={styles.Label}>{grill.name}</p>
          <span className={styles.Value}>
            {priceFormat.format((grill.value || 0) * grill.ticketCount)}
          </span>
        </div>
      )}

      {canopy && (
        <div className={`${styles.Row} ${styles.GrillRow}`}>
          <p className={styles.Label}>{`${canopy.name} × ${canopy.ticketCount}`}</p>
          <span className={styles.Value}>
            {priceFormat.format((canopy.value || 0) * canopy.ticketCount)}
          </span>
        </div>
      )}

      <div className={styles.Row}>
        <p className={styles.Label}>{t('pages.ticketSummary.subTotal')}</p>
        <span className={styles.Value}>{priceFormat.format(subTotal)}</span>
      </div>

      {(discountAmount > 0 || giftCardAmount > 0) && (
        <>
          <Divider className={styles.Divider} />
          <div className={styles.Deductions}>
            {discountAmount > 0 && (
              <div className={styles.Row}>
                <p className={styles.Label}>
                  {t('pages.ticketSummary.discount.summaryDiscount')}
                </p>
                <span className={styles.Deduction}>
                  -{priceFormat.format(discountAmount)}
                </span>
              </div>
            )}
            {giftCardAmount > 0 && (
              <div className={styles.Row}>
                <p className={styles.Label}>
                  {t('pages.ticketSummary.discount.summaryGiftCard')}
                </p>
                <span className={styles.Deduction}>
                  -{priceFormat.format(giftCardAmount)}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      <Divider className={styles.Divider} />

      <div className={styles.PaymentSection}>
        <h3 className={styles.SectionTitle}>{t('pages.ticketSummary.paymentMethod')}</h3>
        {paymentMethods.map((method) => (
          <button
            className={`${styles.PaymentMethod} ${
              method.id === selectedPaymentMethodId ? styles.PaymentMethodSelected : ''
            }`}
            type="button"
            key={method.id}
            onClick={() => onSelectPaymentMethod(method.id)}
          >
            <span className={styles.PaymentMethodName}>
              {t(`pages.ticketSummary.payment.${method.id}`)}
            </span>
            <img
              className={styles.PaymentMethodLogo}
              src={method.logo}
              alt={t(`pages.ticketSummary.payment.${method.id}`)}
            />
          </button>
        ))}
      </div>

      <Divider className={styles.Divider} />

      <div className={styles.TotalSection}>
        <div className={styles.TotalRow}>
          <p className={styles.TotalLabel}>{t('pages.ticketSummary.total')}</p>
          <h2 className={styles.TotalValue}>{priceFormat.format(total)}</h2>
        </div>
        <Button
          className={styles.PayButton}
          size="large"
          disabled={!canPay}
          loading={loading}
          onClick={onPay}
        >
          {t('pages.ticketSummary.pay')}
        </Button>
      </div>
    </div>
  );
};

export default PurchaseSummaryCard;
